import React, { useEffect, useMemo, useRef, useState } from 'react';

import { close, down } from '@/assets/icons';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { Chip } from '@/components/shared/Chip';
import { SearchInput } from '@/components/shared/Inputs';
import { getRotationStatusClassName } from '@/components/shared/RotationStatusLabel';
import { RoundedButton } from '@/components/shared/RoundedButton';
import {
	DefaultKeys,
	ENERGY_TAGS,
	ROTATION_STATUSES,
	SONG_TYPE_TAGS,
} from '@/config/constants';
import { TagsByCategoryFilter } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { LimitedTeamSong } from '@/providers/TeamSongsProvider';
import { SvgIcon } from '@material-ui/core';

import styles from './SearchAndFilter.module.scss';

export enum FilterEnum {
	ROTATION_STATUS = 'ROTATION_STATUS',
	ENERGY = 'ENERGY',
	SONG_TYPE = 'SONG_TYPE',
	DEFAULT_KEY = 'DEFAULT_KEY',
}

export type ViewByValue = 'active' | 'seasonal' | 'all';

const VIEW_BY_OPTIONS: { label: string; value: ViewByValue }[] = [
	{ label: 'Active', value: 'active' },
	{ label: 'Seasonal', value: 'seasonal' },
	{ label: 'All', value: 'all' },
];

// Rotation statuses that belong to "Active" pre-scope
const ACTIVE_ROTATION_STATUSES = new Set(['high', 'regular', 'low', 'occasional']);

export const convertFilterEnumToSongKey = (
	selectedTags: SelectedTagsObject,
): TagsByCategoryFilter => ({
	emotionTags: [],
	energyTags: selectedTags[FilterEnum.ENERGY],
	gospelTags: [],
	songTypeTags: selectedTags[FilterEnum.SONG_TYPE],
});

export const FilterItems: Record<
	FilterEnum,
	{
		label: string;
		capitalisedLabel: string;
		leadingComponent?: React.ReactElement;
	}[]
> = {
	[FilterEnum.ROTATION_STATUS]: ROTATION_STATUSES.map(
		({ key, capitalisedLabel }) => ({
			label: key,
			capitalisedLabel,
			leadingComponent: (
				<div
					className={classNames([
						styles['rotation-circle'],
						getRotationStatusClassName(key),
					])}
				/>
			),
		}),
	),
	[FilterEnum.ENERGY]: ENERGY_TAGS.map(({ label, capitalisedLabel }) => ({
		label,
		capitalisedLabel,
	})),
	[FilterEnum.SONG_TYPE]: SONG_TYPE_TAGS.map(
		({ label, capitalisedLabel }) => ({
			label,
			capitalisedLabel,
		}),
	),
	[FilterEnum.DEFAULT_KEY]: DefaultKeys.map((key) => ({
		label: key,
		capitalisedLabel: key,
	})),
};

export type SelectedTagsObject = Record<FilterEnum, string[]>;

export const emptySelectedTags: SelectedTagsObject = {
	[FilterEnum.ENERGY]: [],
	[FilterEnum.ROTATION_STATUS]: [],
	[FilterEnum.SONG_TYPE]: [],
	[FilterEnum.DEFAULT_KEY]: [],
};

const CHIP_LABELS: Record<FilterEnum, string> = {
	[FilterEnum.ROTATION_STATUS]: 'Rotation',
	[FilterEnum.ENERGY]: 'Energy',
	[FilterEnum.SONG_TYPE]: 'Song Type',
	[FilterEnum.DEFAULT_KEY]: 'Default Key',
};

const getStaticOptions = (
	filterEnum: FilterEnum,
): { label: string; value: string }[] =>
	FilterItems[filterEnum].map((item) => ({
		label: item.capitalisedLabel,
		value: item.label,
	}));

export const getSongFieldFromFilterEnum = (
	song: LimitedTeamSong,
	filter?: FilterEnum,
): string[] => {
	switch (filter) {
		case FilterEnum.ENERGY:
			return song['energyTags'];
		case FilterEnum.ROTATION_STATUS:
			return [song['rotationStatus'] || 'regular'];
		case FilterEnum.SONG_TYPE:
			return song['songTypeTags'];
		case FilterEnum.DEFAULT_KEY:
			return [song['defaultKey'] || ''];
		default:
			return [];
	}
};

const getAvailableValues = (
	filterEnum: FilterEnum,
	songs: LimitedTeamSong[],
	selectedTags: SelectedTagsObject,
): Set<string> => {
	const otherFilters = Object.values(FilterEnum).filter((f) => f !== filterEnum);
	const filtered = songs.filter((song) =>
		otherFilters.every((f) => {
			const active = selectedTags[f];
			if (active.length === 0) return true;
			const songValues = getSongFieldFromFilterEnum(song, f);
			return active.some((v) => songValues.includes(v));
		}),
	);
	return new Set(
		filtered
			.flatMap((song) => getSongFieldFromFilterEnum(song, filterEnum))
			.filter(Boolean),
	);
};

type ViewByChipProps = {
	value: ViewByValue;
	onSelect: (value: ViewByValue) => void;
};

const ViewByChip: React.FC<ViewByChipProps> = ({ value, onSelect }) => {
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);

	// MUI Popover calculates position at open time and ignores CSS transitions. When the clear button
	// animates in/out, chips shift but the open dropdown doesn't follow. Dispatching a resize event
	// 210ms after value changes (post-transition) forces MUI to recalculate. Fragile but functional.
	useEffect(() => {
		if (!anchorEl) return;
		const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 210);
		return () => clearTimeout(t);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const selectedOption = VIEW_BY_OPTIONS.find((opt) => opt.value === value);
	const displayLabel = `View: ${selectedOption?.label ?? 'Active'}`;
	const chevron = <SvgIcon component={down} style={{ fontSize: 14 }} />;

	const panel = (
		<div className={styles['multi-select-panel']}>
			{VIEW_BY_OPTIONS.map((opt) => (
				<div
					key={opt.value}
					className={styles['multi-select-row']}
					onClick={() => {
						onSelect(opt.value);
						setAnchorEl(null);
					}}
				>
					<div
						className={classNames([
							styles['radio-dot'],
							value === opt.value && styles['radio-dot--checked'],
						])}
					/>
					<span>{opt.label}</span>
				</div>
			))}
		</div>
	);

	return (
		<>
			<Chip
				variant="rounded"
				selected={true}
				className={styles['chip--active']}
				trailingComponent={chevron}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				{displayLabel}
			</Chip>
			<AsaphDropdownMenu
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				items={[]}
				customContent={panel}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			/>
		</>
	);
};

type MultiSelectChipProps = {
	label: string;
	options: { label: string; value: string }[];
	selected: string[];
	onToggle: (value: string) => void;
	availableValues?: Set<string>;
};

const MultiSelectChip: React.FC<MultiSelectChipProps> = ({
	label,
	options,
	selected,
	onToggle,
	availableValues,
}) => {
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);

	// MUI Popover calculates position at open time and ignores CSS transitions. When the clear button
	// animates in/out, chips shift but the open dropdown doesn't follow. Dispatching a resize event
	// 210ms after selection changes (post-transition) forces MUI to recalculate. Fragile but functional.
	useEffect(() => {
		if (!anchorEl) return;
		const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 210);
		return () => clearTimeout(t);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected]);

	const isActive = selected.length > 0;
	const displayLabel = isActive ? `${label}: ${selected.length}` : label;
	const chevron = <SvgIcon component={down} style={{ fontSize: 14 }} />;

	const panel = (
		<div className={styles['multi-select-panel']}>
			{options.length === 0 ? (
				<div className={styles['multi-select-empty']}>No options</div>
			) : (
				options.map((opt) => {
					const isUnavailable =
						availableValues !== undefined &&
						!availableValues.has(opt.value) &&
						!selected.includes(opt.value);
					return (
						<div
							key={opt.value}
							className={classNames([
								styles['multi-select-row'],
								isUnavailable && styles['multi-select-row--disabled'],
							])}
							onClick={isUnavailable ? undefined : () => onToggle(opt.value)}
						>
							<input
								type="checkbox"
								readOnly
								checked={selected.includes(opt.value)}
							/>
							<span>{opt.label}</span>
						</div>
					);
				})
			)}
		</div>
	);

	return (
		<>
			<Chip
				variant="rounded"
				selected={isActive}
				className={isActive ? styles['chip--active'] : styles['chip--default']}
				trailingComponent={chevron}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				{displayLabel}
			</Chip>
			<AsaphDropdownMenu
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				items={[]}
				customContent={panel}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			/>
		</>
	);
};

export type FilterBarOptions = Partial<
	Record<FilterEnum, { label: string; value: string }[]>
>;

export type SearchAndFilterProps = {
	className?: string;
	placeholder?: string;
	onChange: (filters: {
		searchTerm: string;
		selectedTags: SelectedTagsObject;
		viewBy: ViewByValue;
	}) => void;
	clearFilters?: number;
	filtersClassName?: string;
	excludeFilters?: FilterEnum[];
	noPadding?: boolean;
	hideViewBy?: boolean;
	filterOptions?: FilterBarOptions;
	songs?: LimitedTeamSong[];
	creatorOptions?: { label: string; value: string }[];
	creatorFilter?: string[];
	onCreatorToggle?: (value: string) => void;
};

export const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
	onChange,
	clearFilters,
	className,
	filtersClassName,
	placeholder,
	noPadding = false,
	hideViewBy = false,
	excludeFilters = [],
	filterOptions,
	songs,
	creatorOptions,
	creatorFilter = [],
	onCreatorToggle,
}) => {
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [selectedTags, setSelectedTags] =
		useState<SelectedTagsObject>(emptySelectedTags);
	const [viewBy, setViewBy] = useState<ViewByValue>('active');

	useEffect(() => {
		onChange({ searchTerm, selectedTags, viewBy });
	}, [selectedTags, searchTerm, viewBy, onChange]);

	useEffect(() => {
		if (clearFilters) {
			setSearchTerm('');
			setSelectedTags(emptySelectedTags);
			setViewBy('active');
		}
	}, [clearFilters]);

	const prevViewByRef = useRef<ViewByValue>('active');
	useEffect(() => {
		const prev = prevViewByRef.current;
		prevViewByRef.current = viewBy;
		// Clear Rotation Status selections when switching Active↔Seasonal (neither is 'all')
		if (prev !== 'all' && viewBy !== 'all' && prev !== viewBy) {
			setSelectedTags((s) => ({ ...s, [FilterEnum.ROTATION_STATUS]: [] }));
		}
	}, [viewBy]);

	const handleToggle = (filterEnum: FilterEnum) => (value: string) => {
		setSelectedTags((prev) => {
			const current = prev[filterEnum];
			return {
				...prev,
				[filterEnum]: current.includes(value)
					? current.filter((v) => v !== value)
					: [...current, value],
			};
		});
	};

	const visibleFilters = Object.values(FilterEnum).filter(
		(f) => !excludeFilters.includes(f),
	);

	const filterAvailability = useMemo(() => {
		if (!songs) return null;
		// Apply search pre-filter
		const searchFiltered = searchTerm
			? songs.filter((s) => {
					const term = searchTerm.toLocaleLowerCase();
					return (
						s.name.toLocaleLowerCase().includes(term) ||
						s.artist.toLocaleLowerCase().includes(term)
					);
				})
			: songs;
		// Apply viewBy pre-scope so greying reflects current view mode
		const viewByFiltered = searchFiltered.filter((s) => {
			const status = (s.rotationStatus || 'regular').toLowerCase();
			if (viewBy === 'active') return ACTIVE_ROTATION_STATUSES.has(status);
			if (viewBy === 'seasonal') return status === 'seasonal';
			return true; // 'all'
		});
		const result: Partial<Record<FilterEnum, Set<string>>> = {};
		Object.values(FilterEnum).forEach((filterEnum) => {
			result[filterEnum] = getAvailableValues(filterEnum, viewByFiltered, selectedTags);
		});
		return result;
	}, [songs, selectedTags, searchTerm, viewBy]);
	const hasActiveFilters = Object.values(selectedTags).some(
		(tags) => tags.length > 0,
	) || creatorFilter.length > 0;
	const xIcon = <SvgIcon component={close} style={{ fontSize: 12 }} />;

	return (
		<>
			<div
				className={classNames([
					styles['root'],
					!noPadding && styles['padding'],
					className,
				])}
			>
				<SearchInput
					id="search"
					name="search"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					clearSearchQuery={() => setSearchTerm('')}
					placeholder={placeholder}
					fullWidth
				/>
			</div>

			{visibleFilters.length > 0 && (
				<div
					className={classNames([
						styles['filter-bar'],
						!noPadding && styles['padding'],
						filtersClassName,
					])}
				>
					<div className={styles['chips-scroll']}>
						<div
							className={classNames([
								styles['clear-button-sticky'],
								hasActiveFilters && styles['clear-button-sticky--visible'],
							])}
						>
							<RoundedButton
								color="default"
								leadingComponent={xIcon}
								onClick={() => { setSelectedTags(emptySelectedTags); setViewBy('active'); }}
							>
								Clear
							</RoundedButton>
						</div>
						{!hideViewBy && <ViewByChip value={viewBy} onSelect={setViewBy} />}
						{visibleFilters.map((filterEnum) => (
							<MultiSelectChip
								key={filterEnum}
								label={CHIP_LABELS[filterEnum]}
								options={
									filterOptions?.[filterEnum] ??
									getStaticOptions(filterEnum)
								}
								selected={selectedTags[filterEnum]}
								onToggle={handleToggle(filterEnum)}
								availableValues={filterAvailability?.[filterEnum]}
							/>
						))}
						{creatorOptions && onCreatorToggle && (
							<MultiSelectChip
								label="Creator"
								options={creatorOptions}
								selected={creatorFilter}
								onToggle={onCreatorToggle}
							/>
						)}
					</div>
				</div>
			)}
		</>
	);
};
